'use client'

import { ReactNode } from 'react'
import { cn } from '@/lib/utils'

/* ---------------------------------------------------------------- shell */

const WindowShell = ({ title, children, className }: { title: string; children: ReactNode; className?: string }) => (
    <div
        className={cn(
            'overflow-hidden rounded-[1.25rem] border border-border/70 bg-[hsl(var(--deep-black))] shadow-[var(--shadow-card)]',
            className,
        )}
    >
        <div className="flex items-center gap-2 border-b border-border/60 bg-[hsl(var(--charcoal))] px-4 py-2.5">
            <span className="h-2 w-2 rounded-full bg-primary/70" />
            <span className="h-2 w-2 rounded-full bg-foreground/20" />
            <span className="h-2 w-2 rounded-full bg-foreground/20" />
            <span className="ml-2 truncate text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">{title}</span>
        </div>
        <div className="p-4 sm:p-5">{children}</div>
    </div>
)

export const PhoneMock = ({
    title = 'Приложение компании',
    children,
    className,
}: {
    title?: string
    children: ReactNode
    className?: string
}) => (
    <div className={cn('mx-auto w-full max-w-[300px]', className)}>
        <div className="rounded-[2.25rem] border border-border/70 bg-[hsl(var(--charcoal))] p-2.5 shadow-[var(--shadow-card)]">
            <div className="relative overflow-hidden rounded-[1.85rem] bg-[hsl(var(--deep-black))]">
                <div className="flex items-center justify-between px-5 pb-1 pt-3 text-[10px] text-muted-foreground">
                    <span>9:41</span>
                    <span className="h-1.5 w-14 rounded-full bg-foreground/15" />
                    <span>100%</span>
                </div>
                <div className="px-4 pb-5 pt-2">
                    <p className="mb-3 text-[11px] font-bold uppercase tracking-[0.14em] text-primary">{title}</p>
                    <div className="flex flex-col gap-2.5">{children}</div>
                </div>
            </div>
        </div>
    </div>
)

const Row = ({ label, value, accent }: { label: string; value?: string; accent?: boolean }) => (
    <div
        className={cn(
            'flex items-center justify-between gap-3 rounded-xl border px-3 py-2.5 text-xs',
            accent ? 'border-primary/40 bg-primary/10' : 'border-border/50 bg-[hsl(var(--charcoal))]',
        )}
    >
        <span className={cn('truncate font-semibold', accent ? 'text-primary' : 'text-foreground/85')}>{label}</span>
        {value && <span className="shrink-0 text-muted-foreground">{value}</span>}
    </div>
)

export const MockRow = Row

/* ------------------------------------------------------- CRM client card */

export const CrmClientCard = ({ className }: { className?: string }) => (
    <WindowShell title="CRM · карточка клиента" className={className}>
        <div className="flex items-start gap-3 border-b border-border/50 pb-4">
            <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/15 text-sm font-extrabold text-primary">
                АП
            </span>
            <div className="min-w-0 flex-1">
                <p className="text-base font-extrabold text-foreground">Анна Петрова</p>
                <p className="text-xs text-muted-foreground">Клиент с 2024 · создана менеджером после звонка</p>
            </div>
            <span className="pill pill-live shrink-0">Активна</span>
        </div>

        <div className="mt-4 grid gap-2.5 sm:grid-cols-2">
            <Row label="Заявки" value="4" />
            <Row label="Сделки" value="2 в работе" accent />
            <Row label="Заказы" value="7" />
            <Row label="Оплаты" value="все закрыты" />
            <Row label="Сообщения" value="12" />
            <Row label="Задачи" value="1 на сегодня" accent />
            <Row label="Документы" value="3 файла" />
            <Row label="Бонусы" value="1 250 баллов" />
        </div>

        <div className="mt-4 rounded-xl border border-border/50 bg-[hsl(var(--charcoal))] p-3">
            <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.14em] text-muted-foreground">История</p>
            <ul className="flex flex-col gap-1.5 text-xs text-foreground/80">
                <li>Сегодня · оплатила заказ №1042</li>
                <li>Вчера · написала в чат приложения</li>
                <li>3 дня назад · менеджер поставил задачу «перезвонить»</li>
                <li>Месяц назад · зарегистрировалась в приложении</li>
            </ul>
        </div>
    </WindowShell>
)

/* --------------------------------------------------------- sales pipeline */

const STAGES = [
    { title: 'Новая заявка', note: 'менеджер назначен автоматически', count: '12' },
    { title: 'Квалификация', note: 'уточняем задачу клиента', count: '7' },
    { title: 'Предложение', note: 'отправлено из карточки', count: '5' },
    { title: 'Счёт', note: 'документ и оплата рядом', count: '3' },
    { title: 'Оплата', note: 'статус приходит из платежей', count: '2' },
    { title: 'Завершение', note: 'клиент остаётся в базе', count: '9' },
]

export const PipelineMock = () => (
    <WindowShell title="CRM · процесс продажи">
        <div className="grid gap-2.5 sm:grid-cols-2 lg:grid-cols-3">
            {STAGES.map((s, i) => (
                <div
                    key={s.title}
                    className={cn(
                        'rounded-xl border p-3',
                        i === 3 ? 'border-primary/45 bg-primary/10' : 'border-border/50 bg-[hsl(var(--charcoal))]',
                    )}
                >
                    <div className="flex items-center justify-between">
                        <p className={cn('text-xs font-bold', i === 3 ? 'text-primary' : 'text-foreground')}>{s.title}</p>
                        <span className="text-[11px] text-muted-foreground">{s.count}</span>
                    </div>
                    <p className="mt-1 text-[11px] leading-relaxed text-muted-foreground">{s.note}</p>
                </div>
            ))}
        </div>
    </WindowShell>
)

/* ------------------------------------------------------------------ chat */

export const ChatMock = () => (
    <WindowShell title="Чат · клиент рядом">
        <div className="grid gap-4 lg:grid-cols-[1.4fr_1fr]">
            <div className="flex flex-col gap-2.5">
                <div className="max-w-[85%] rounded-2xl rounded-tl-md border border-border/50 bg-[hsl(var(--charcoal))] px-3.5 py-2.5 text-xs text-foreground/85">
                    Здравствуйте! Хочу перенести визит на пятницу.
                </div>
                <div className="ml-auto max-w-[85%] rounded-2xl rounded-tr-md bg-primary/15 px-3.5 py-2.5 text-xs text-primary">
                    Перенёс на пятницу, 15:00. Отправил напоминание в приложение.
                </div>
                <div className="max-w-[85%] rounded-2xl rounded-tl-md border border-border/50 bg-[hsl(var(--charcoal))] px-3.5 py-2.5 text-xs text-foreground/85">
                    Спасибо! И счёт пришлите, пожалуйста.
                </div>
                <div className="ml-auto max-w-[85%] rounded-2xl rounded-tr-md bg-primary/15 px-3.5 py-2.5 text-xs text-primary">
                    Счёт из сделки №318 — во вложении.
                </div>
            </div>
            <aside className="rounded-xl border border-border/50 bg-[hsl(var(--charcoal))] p-3">
                <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.14em] text-muted-foreground">Клиент</p>
                <p className="text-sm font-bold text-foreground">Анна Петрова</p>
                <ul className="mt-2 flex flex-col gap-1.5 text-[11px] text-foreground/80">
                    <li>Сделка №318 · этап «Счёт»</li>
                    <li>Последний заказ · 12 дней назад</li>
                    <li>Бонусы · 1 250</li>
                    <li>Задача · перезвонить в пятницу</li>
                </ul>
                <span className="mt-3 inline-flex text-[11px] font-semibold text-primary">Открыть карточку →</span>
            </aside>
        </div>
    </WindowShell>
)

/* ------------------------------------------------------------ site admin */

export const SiteAdminMock = () => (
    <WindowShell title="Админка сайта">
        <div className="grid gap-4 sm:grid-cols-[0.8fr_1.2fr]">
            <nav className="flex flex-col gap-1.5">
                {['Страницы', 'Контент', 'Каталог', 'Услуги', 'Баннеры', 'Формы', 'Заявки'].map((i, idx) => (
                    <span
                        key={i}
                        className={cn(
                            'rounded-lg px-3 py-2 text-xs font-semibold',
                            idx === 6 ? 'bg-primary/15 text-primary' : 'text-foreground/75 hover:bg-foreground/5',
                        )}
                    >
                        {i}
                    </span>
                ))}
            </nav>
            <div className="flex flex-col gap-2.5">
                <Row label="Заявка · Ирина, «Консультация»" value="в CRM" accent />
                <Row label="Заявка · Пётр, «Каталог»" value="менеджер назначен" />
                <Row label="Товар · обновлён остаток" value="из 1С" />
                <Row label="Баннер на главной" value="опубликован" />
                <Row label="Страница «Услуги»" value="черновик" />
            </div>
        </div>
    </WindowShell>
)

/* ------------------------------------------------------ automation editor */

export const AutomationMock = () => (
    <WindowShell title="Сценарий автоматизации">
        <div className="flex flex-col gap-3">
            <div className="rounded-xl border border-primary/40 bg-primary/10 p-3">
                <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-primary">Если</p>
                <p className="mt-1 text-sm font-bold text-foreground">Клиент оставил заявку</p>
                <p className="text-[11px] text-muted-foreground">источник: приложение, сайт, менеджер</p>
            </div>
            <div className="rounded-xl border border-border/50 bg-[hsl(var(--charcoal))] p-3">
                <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-muted-foreground">То</p>
                <div className="mt-2 flex flex-col gap-2">
                    {[
                        'Создать сделку',
                        'Назначить менеджера',
                        'Создать задачу «связаться»',
                        'Отправить клиенту сообщение',
                        'Через 24 часа проверить статус',
                    ].map((a, i) => (
                        <div
                            key={a}
                            className="flex items-center gap-2 rounded-lg border border-border/40 px-3 py-2 text-xs text-foreground/85"
                        >
                            <span className="flex h-5 w-5 items-center justify-center rounded-md bg-primary/15 text-[10px] font-bold text-primary">
                                {i + 1}
                            </span>
                            {a}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    </WindowShell>
)

/* ----------------------------------------------------------- integrations */

export const IntegrationBridge = ({ external, items, note }: { external: string; items: string[]; note?: string }) => (
    <div className="rounded-2xl border border-border/60 bg-[hsl(var(--deep-black))] p-5">
        <div className="flex flex-col items-stretch gap-3 sm:flex-row sm:items-center">
            <span className="rounded-xl border border-primary/40 bg-primary/10 px-4 py-2.5 text-center text-xs font-bold text-primary">
                VCENTRE.ONE
            </span>
            <span className="mx-auto text-center text-xs text-muted-foreground sm:mx-0">↔</span>
            <span className="rounded-xl border border-border/60 bg-[hsl(var(--charcoal))] px-4 py-2.5 text-center text-xs font-bold text-foreground">
                {external}
            </span>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
            {items.map((i) => (
                <span key={i} className="rounded-full border border-border/50 px-3 py-1 text-[11px] text-foreground/80">
                    {i}
                </span>
            ))}
        </div>
        {note && <p className="mt-3 text-xs text-muted-foreground">{note}</p>}
    </div>
)

/* -------------------------------------------------------------- segments */

export const SegmentMock = () => (
    <WindowShell title="Рассылка · от сегмента к результату">
        <div className="grid gap-3 sm:grid-cols-4">
            {[
                { t: 'Сегмент', v: 'Не покупали 60 дней' },
                { t: 'Сообщение', v: 'Персональное предложение' },
                { t: 'Действие', v: 'Открыть каталог в приложении' },
                { t: 'Результат', v: 'Заявка и сделка в CRM' },
            ].map((s, i) => (
                <div
                    key={s.t}
                    className={cn(
                        'rounded-xl border p-3',
                        i === 3 ? 'border-primary/45 bg-primary/10' : 'border-border/50 bg-[hsl(var(--charcoal))]',
                    )}
                >
                    <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-muted-foreground">{s.t}</p>
                    <p className={cn('mt-1 text-xs font-semibold', i === 3 ? 'text-primary' : 'text-foreground')}>{s.v}</p>
                </div>
            ))}
        </div>
    </WindowShell>
)

export { WindowShell }
